/**
 * @fileoverview Types for widget parameters and configurations.
 * Storybook parses the jSdoc comments in this file to generate documentation.
 * Please comment any new params
 * @see https://fwn.atlassian.net/wiki/spaces/WP/pages/26640392/Widget+Parameters#a11y_disable_focus
 * @see http://localhost:6006/?path=/docs/documentation-widget-paramaters--docs
 */
import type { CSSProperties } from 'react'

import type { PIPParams, PIPTarget } from '@core/pictureInPicture/types'
import type { AppContextType } from '@embed/components/helpers'

export type PlayerStyle = 'classic' | 'modern'
export type PlayerMinimizedStyle =
  'minimal' | 'branded_handle' | 'live_badge_watch'

export type ButtonBorderStyle = 'default' | 'rounded' | 'hard'

export enum WidgetTypesEnum {
  EMBED_FEED = 'embed_feed',
  STORYBLOCK = 'storyblock',
  STORYLINK = 'storylink',
  HEROUNIT = 'herounit',
  CHANNEL_BUTTON = 'channel_button',
  ADD_TO_CALENDAR = 'add_to_calendar',
  SHARE = 'share',
  PROGRAMMATIC = 'programmatic',
  PLAYER = 'player',
  UNKNOWN = 'unknown',
  VIDEO_CHAT = 'video_chat',
}

export enum IPresentation {
  ADD_TO_CALENDAR = 'add_to_calendar',
  CAROUSEL = 'carousel',
  CIRCLE_STORIES = 'circle_stories',
  DYNAMIC_CAROUSEL = 'dynamic_carousel',
  HORIZONTAL_CAROUSEL = 'horizontal_carousel',
  PLAYER_DECK = 'player_deck',
  GRID = 'grid',
  HERO = 'hero',
  PLAYER = 'player',
  SHARE = 'share',
  STORYBLOCK = 'storyblock',
  STORYBLOCK_PRODUCT_GALLERY = 'storyblock_product_gallery',
  STORYLINK = 'storylink',
  VERTICAL_STORIES = 'vertical_stories',
}

export enum PlayerPlacementEnum {
  TOP_LEFT = 'top-left',
  TOP_RIGHT = 'top-right',
  BOTTOM_LEFT = 'bottom-left',
  BOTTOM_RIGHT = 'bottom-right',
}

export enum ResetTransformEnum {
  ALWAYS = 'always',
  FULLSCREEN = 'fullscreen',
  NEVER = 'never',
}

/** Livestream: when to show product card on the video (carousel / highlight / modal). */
export enum ProductCardLsEnum {
  ALWAYS = 'always',
  FULLSCREEN = 'fullscreen',
  FALSE = 'false',
}

export enum OpenInEnum {
  DEFAULT = 'default',
  BLANK = '_blank',
  SELF = '_self',
  MODAL = '_modal',
  IFRAME = '_iframe',
  FALSE = 'false',
}

export enum ShareEnum {
  HASH = 'hash',
  QUERY = 'query',
  FALSE = 'false',
}

export enum ShoppingCartStyle {
  BAG = 'bag',
  CART = 'cart',
}

export enum UsernamePromptBehaviorEnum {
  OFF = 'off',
  CHAT = 'chat',
  ACTIVE = 'active',
  TRUE = 'true', //fallback for when livestream_prompt_username_change was a bool. "true" == "chat"
}

export type ProductClickHandlerType = 'internal' | 'external'

export enum ThumbnailShapeEnum {
  CIRCLE = 'circle',
  SQUARE = 'square',
  RECTANGLE = 'rectangle',
}

export enum PlayerNativeScrollEnum {
  Vertical = 'vertical',
  Horizontal = 'horizontal',
  Disabled = 'false',
}

export type IParams = {
  // TODO - remove attribute ab_test when no production widgets are using this attribute,
  // or when all production widgets switched 100% to fw-widget
  ab_test?: boolean
  ad_positions?: string
  ads_hide_badge?: boolean
  /**
   * @public
   * Disable widget programmatic focus.
   * @default false
   */
  a11y_disable_focus?: boolean
  /**
   * @public
   * Disable thumbnail preview autoplay in embed feed
   * @default false
   */
  a11y_disable_previews?: boolean
  /**
   * @public
   * Limit autoplaying thumbnail previews to 5 seconds. This allows you to keep the autoplay, but
   * satisfy WCAG play,pause,hide criteria
   * @default false
   */
  a11y_limit_previews?: boolean
  ad_channel_id?: string
  allow_duplicate_ads?: boolean
  /**
   * @public
   * Whether to use edge cache for the API calls.
   * @default true
   */
  api_cache?: boolean
  /**
   * @public
   * The nonce for the edge cache for invalidating by integration if needed.
   * @default 'some string'
   */
  api_cache_nonce?: string
  /**
   * @public
   * Override the edge cache host used when `api_cache` is enabled. When unset,
   * the cache host is derived from `api_host`.
   */
  api_cache_host?: string
  api_host?: string
  api_host_ads?: string
  /**
   * @public
   * Override the CDN host for all video and image assets. Accepts a
   * comma-separated list of hostnames for round-robin distribution
   * (e.g. "cdn-a.example.com,cdn-b.example.com"). Only affects URLs that
   * already use a `cdn{n}.` prefix. Takes precedence over channel-based CDN
   * overrides.
   */
  cdn_host?: string
  api_host_tracking?: string
  api_host_tracking_legacy?: string
  auto_apply_coupon_enabled?: boolean // [GROW-641] feature flag for auto_apply_coupon_code
  auto_subtitles_enabled?: boolean // [CS-7834] subtitles are enabled by default when video is inline. This toggles that
  autoplay?: boolean
  /**
   * @public
   * Duration in seconds for how long thumbnail autoplay should play before stopping or advancing.
   * When `a11y_limit_previews` is also set, the effective duration is the shorter of the two.
   * @default undefined
   */
  autoplay_duration?: number
  autoplay_keep_thumbnail?: boolean // [CS-3108] Determines whether to show thumbnail back on mouse leave
  /**
   * @public
   * Whether thumbnail autoplay should loop the same video or advance to the next visible thumbnail.
   * When `false`, only visible thumbnails will autoplay sequentially; after the last one finishes,
   * it wraps back to the first.
   * @default true
   */
  autoplay_loop?: boolean
  branding?: boolean
  /**
   * The business ID associated with the widget. This will be used for A/B test framework as an indication for opting into A/B tests. It will be used in the global script
   */
  business_id?: string
  channel?: string
  channel_id?: string
  cookies_days?: number // [CS-4775] to set cookies expiration time (defaults to 365 days)
  cookies_opt_in?: boolean // [CS-4137] to turn on cookies opt-in management (when enabled, cookies postponed until consent is given)
  cookies_restore_consent?: boolean // [CS-13409] whether granted cookie/storage consent levels persisted in the `fw_cc` cookie should be re-applied on the next page load (defaults to `true`, set to `false` to disable)
  cookies_ephemeral?: boolean // [CS-15003] redirect localStorage to sessionStorage and write cookies without `expires` (session cookies); use the script-tag form when possible as storage reads can happen during module init
  cookies_root?: boolean // [CS-4462] whether to set up tracking cookies for the root domain (defaults to `true`)
  cta_button_type?: string
  /**
   * @public
   * Domain Assistant ID for AVA integration
   */
  domain_assistant_id?: string
  dir?: '' | 'ltr' | 'rtl'
  disable_auto_heart?: boolean
  /**
   * When false, heart button shows only heart and click sends directly.
   * When true or undefined, click opens emoji picker; sending happens on emoji selection.
   * @default true
   */
  live_stream_allow_multiple_emojis?: boolean
  email_embed?: boolean // [CS-4515] Configurable player menu
  expand_prod_desc_enabled?: boolean // [GROW-392] expand product description when user scrolls to bottom
  force_replay?: boolean // [CS-8144] Ignore video.replay_enabled
  full_height_pdp?: boolean // [GROW-1040] Full-height PDP
  fullscreen_pdp_enabled?: boolean // [GROW-827] Default value is false until it is green lit to be GA'd
  handle_product_card_click?: ProductClickHandlerType // [CS-6389] whether or not to handle product card clicks internally
  handle_product_cta_click?: ProductClickHandlerType // [CS-6389] whether or not to handle product CTA clicks internally
  hashtag?: string
  hide_cart_quantity?: boolean // [GROW-498] hide cart line item quantity
  hide_cart_total?: boolean // [GROW-498] hide cart total
  hide_product_cents?: boolean // [CS-11253] Default value is false.
  hide_product_price?: boolean // [GROW-631] Default value is true. Customers can now toggle to hide the product price when creating custom products in the business portal. This is a placeholder for a naboo flag to be added in case this feature needs to be turned off.
  /**
   * @public
   * [CS-9158] Hide play button in the thumbnail.
   * @default false
   */
  hide_play_button?: boolean
  hide_show_detail?: boolean // Hide `show detail` button in the product card. Requested in https://bit.ly/3qZE5N6.
  homepage_url?: string
  in_sdk_webview?: boolean
  /**
   * Whether to inject JSON-LD schema for the first video in the feed to the page header.
   * @default false
   */
  jsonld?: boolean
  /**
   * Video ID to scroll to after the player loads. Used during Soft-PIP initialization.
   * @default undefined
   */
  key_moments?: boolean // Default value is false until the feature is turned on for all customers
  lang?: string // [CS-1025] set specific language for widget
  lazy?: boolean // [CS-11335] Whether to lazy load the widget when it is visible in the viewport. Defaults to false.
  /**
   * Widget layout for `<fw-widget>` (from embed config API).
   * fw-widget values include storyblock, carousel_row, circle_stories, etc.
   */
  layout?: string
  light_mode_pdp?: boolean // [COM-1820] Mobile pdp is updated to have a white background by default to test the impact on conversion
  livestream_allow_username_change?: boolean // [CS-4099] Allow users to disable username changing
  livestream_chat?: boolean // [CS-1292] to turn off chat for performance testing
  livestream_firstparty?: boolean // [CS-3532] Enable player in first party context regardless of video settings
  livestream_prompt_username_change?: UsernamePromptBehaviorEnum | null // [CS-2959] When to prompt users to enter a username
  livestream_username?: string // [CS-3216] Client wants to hydrate visitor's username
  livestream_invitation_type?: 'host' | 'guest' // Controls UI of co-host (host) for livestream from web. Defaults to `guest`
  /**
   * Enables dimension-based IVS adaptive quality management. Mirrors the player_livestream_ivs_adaptive_quality feature flag.
   * @default undefined
   */
  livestream_ivs_adaptive_quality?: boolean
  /**
   * Max quality bitrate for IVS adaptive quality. Enables bitrate-based quality capping independently of the adaptive quality flag.
   * @default undefined
   */
  livestream_ivs_max_quality_bitrate?: number
  location?: Location
  loop?: boolean // Player will loop the feed when end is reached (defaults to true)
  loop_video?: boolean // Player will loop the video until the viewer moves to the next video (defaults to false)
  lsdesktop?: boolean
  max_videos?: number
  mode?: string
  moderator_icon?: string // [CS-2547] moderator icon src value
  muted?: boolean // [QA-1191] Whether to unmute on user interaction (fullscreen, thumbnail...). Note: Player always un-mutes on direct user interaction with mute button.
  name?: string
  open_in?: OpenInEnum
  overlay_play_button_size?: number
  page_type?: string
  page_url?: string
  persist_cart?: boolean // [GROW-324] to turn on cart persistency for clients w/o product hydration
  pip_mode?: 'iframe' | 'soft' // Widget's specific PIP mode for customers who want to use Soft PIP only. Empty or `iframe` value will use iframe to render original bellow the video for seamless experience.
  placement?: string
  player_add_to_calendar?: boolean // [CS-1500] to turn off the Add to calendar
  player_captions?: boolean // [CS-1479] to turn off the player caption
  /**
   * @public
   * The type of player controls to use for "long form" videos
   * 'compact' = play / pause, seek
   * 'full' = play / pause, skip back, skip forward, seek, time
   * @default 'compact'
   * **/
  player_controls?: 'compact' | 'full'
  /**
   * @public
   * Whether to hide controls when user idle
   * @default false
   */
  player_controls_hide?: boolean
  /**
   * @public
   * Whether the player can be closed from fullscreen/minimized mode.
   * Default behavior allows player to be closed from fullscreen on large screens
   * and from a minimized layout. Small screens in fullscreen will provide `minimize`
   * button only making `close` attempt a two step action.
   * @default undefined
   * @see https://fwn.atlassian.net/browse/CS-9908
   */
  player_close?: boolean
  player_detached?: boolean // [CS-1930] opt-in to render player for SPA like apps which will handle PIP by themselves
  player_fullscreen?: boolean
  player_fullscreen_strict_mode?: boolean // param to prevent non-fullscreen-button SV video header controls from triggering fullscreen mode
  player_fullscreen_contain?: boolean // Experimental param to force fullscreen player to render video with object-fit: contain
  player_livestream_clip?: boolean // when true, livestream floating player uses a lightweight video wrapper. User interaction will switch wrapper to ivs/agora wrapper
  player_margin?: string
  player_minimize?: boolean // Whether the player can be minimized
  player_more_menu?: boolean
  player_placement?: PlayerPlacementEnum
  player_play_pause?: boolean
  player_scroll_to_video_id?: string
  player_tap_to_unmute?: boolean
  player_tiles?: boolean
  player_unmute?: boolean // [CS-6031] Hide mute button for integrations where there is no audio expected
  player_floating_unmute?: boolean // Show mute/unmute button in floating (minimized) player header. Clicking unmute enters fullscreen and unmutes.
  player_vertical_scroll?: boolean // Enable native vertical scroll navigation for player
  player_native_scroll?: PlayerNativeScrollEnum // [CS-8890] Enable native scroll for player
  /**
   * @public
   * Whether the player start time should be saved when video is playing in fullscreen mode
   * if it is true, the start time will be saved while playing and can be restored when user re-enter the video
   * @default undefined
   * @see https://fwn.atlassian.net/browse/CS-9991
   */
  player_continue_playback?: boolean
  playlist?: string
  playlist_collection?: string
  priority?: string
  product_checkout_card_v2?: boolean // Default value is false, once QA signs off we will swap to true and then wait ~two weeks in case customers do not like this change so we can revert in an agile manner
  product_click_use_instream_pdp?: boolean // [COM-1912] Use instream/outstream PDP on Product Card / List Item click
  product_image_carousel?: boolean // [GROW-996] Product image carousel
  product_index?: boolean // Default value is false, and is nested within the product_checkout_card_v2 FF, so to see this feature both have to be enabled
  product_moment_label?: string // [CS-10002] Product moment label. Defaults to translated "Highlight"
  pulse_animation?: boolean // pulse animation for thumbnails
  microsites_host?: string
  qr_code?: boolean // [CS-4515] Configurable player menu
  referrer?: string
  reverse_audio_controls?: boolean // [CS-7291] Reverse the order of CC and mute buttons in the header
  reset_transform?: ResetTransformEnum
  share?: ShareEnum | null
  share_template?: string | null
  shopping_cart_style?: ShoppingCartStyle // [GROW-434] shopping bag customization for levi's
  shopping_cart_url?: string // [CS-8830] shopping cart url for outstream cart
  shopping_title_disabled?: boolean // [CS-7324] whether to show the shopping wing label
  shopping_quick_add?: boolean // [CS-8918] whether to enable quick add functionality (currently player deck only)
  shopping_quick_view?: boolean // [CS-8918] whether to enable quick view functionality (currently player deck only)
  show_ads?: boolean
  show_add_to_cart_button?: boolean // Show the Add to Cart button on player deck product cards. Default value is false.
  show_currency_prefix?: boolean // [CS-11880] Force consistent currency symbol prefixes (e.g. CN¥, HK$) across all locales
  show_preroll?: boolean
  skus?: string
  sold_and_fulfilled_enabled?: boolean // [PAY-409] enabled sold and fulfilled by on PDP
  sp_outstream_tag?: string // [COM-1883] URL to VAST template with ad outstream configuration used for Criteo
  start_time?: number
  start_time_sku?: string // External ID (SKU) of a product moment to start the video at - seek to product moment
  tap_to_watch?: boolean // [CS-1685] Requires tap to watch when watching livestreams. Defaults to true.
  target?: HTMLElement | ShadowRoot | null
  theme?: string // See also `ui_theme`
  player_deck_layout?: boolean
  player_deck_pip_target?: PIPTarget // [CS-12129] Override pip_target for player deck CTA and product card links
  thumbnail_shape?: ThumbnailShapeEnum // [CS-8890] display embed feed thumbs as circles
  title?: string
  title_alignment?: CSSProperties['textAlign'] // [CS-5493]
  title_size?: CSSProperties['fontSize'] // [CS-5866]
  title_font_weight?: CSSProperties['fontWeight'] // [CS-8880]
  title_font_family?: CSSProperties['fontFamily'] // [CS-8880]
  title_color?: CSSProperties['color'] // [CS-8880]
  type?: WidgetTypesEnum
  ui_asset_back?: string
  ui_asset_close?: string
  ui_asset_closed_captions_disabled?: string
  ui_asset_closed_captions_enabled?: string
  ui_asset_fullscreen?: string
  ui_asset_menu?: string
  ui_asset_minimize?: string
  ui_asset_muted?: string
  ui_asset_next?: string
  ui_asset_pause?: string
  ui_asset_play?: string
  ui_asset_previous?: string
  ui_asset_product_tag?: string
  ui_asset_reaction?: string
  ui_asset_reaction_animation?: string
  ui_asset_shopping_bag?: string
  ui_asset_shopping_cart?: string
  ui_asset_unmuted?: string
  ui_border_style?: ButtonBorderStyle
  ui_button_color?: string | null
  ui_button_font_color?: string | null
  ui_font_css?: string
  ui_full_cta?: boolean
  ui_player_style?: PlayerStyle
  ui_player_minimized_style?: PlayerMinimizedStyle
  ui_primary_color?: string | null
  ui_share_sheet?: string
  ui_theme?: string // UI theme name configurable in the channel appearance settings.
  vast_attrs?: string
  vast_xml?: string
  video?: string
  video_id?: string
  videos?: string // Comma separated list of video IDs to create a videos feed
  /**
   * @public
   * Comma delimited list of video types to filter by when fetching videos for the widget.
   * Possible values: 'live_stream', 'long_form', 'short_video'
   * When undefined, includes short_video and live_stream types.
   * @default undefined
   *
   */
  video_types?: string
  /**
   * @public
   * Integration-provided override for the auto-generated guest ID (`fw_uid`).
   * When set, replaces and persists into the `fw_uid` cookie/localStorage so all
   * downstream consumers (`guest_id`, `visitor_id`) use this value.
   */
  guest_id_override?: string
  /**
   * @public
   * Integration-provided override for the SDK app identity.
   * When set, persists into the `fw_oaauid` cookie/localStorage and is
   * included as root-level `oauth_app_uid` in every event payload.
   */
  oauth_app_id_override?: string
  wait_for_vast_attrs?: boolean // [QA-897] wait for vast_attrs param to be provided before fetching ad tags
  widget_config_id?: string
  widget_id?: string
  widget_presentation?: IPresentation // [CS-8031] New param to control widget presentation (eg. 'product_gallery', 'player_deck')
  /**
   * @public
   * Source of the widget configured through business portal
   * @default undefined
   * @see https://fwn.atlassian.net/browse/CS-9740
   * Possible values: 'auto_embed_shopify', 'recipe_config', 'cohort_config'
   */
  widget_source?: WidgetSourceEnum
} & PIPParams

export enum WidgetSourceEnum {
  AUTO_EMBED_SHOPIFY = 'auto_embed_shopify',
  RECIPE_CONFIG = 'recipe_config',
  COHORT_CONFIG = 'cohort_config',
}

export enum WidgetSizeEnum {
  MEDIUM = 'medium',
  LARGE = 'large',
  SMALL = 'small',
}

export enum ProductTagPlacement {
  TOP = 'top',
  BOTTOM = 'bottom',
  FALSE = 'false',
}

// TODO: Rename to EmbedFeedParams and create EmbedFeedParams as a parent to all widget params
export interface EmbedFeedParams extends Omit<IParams, 'placement'> {
  auto_embed_widget?: boolean // Dynamic param added to auto embed widget
  captions?: boolean
  /**
   * @public
   * Whether to show placeholder skeletons while the grid is loading.
   * @default true
   */
  loading_skeleton?: boolean
  minimized?: boolean
  mode?: 'row' | 'grid' | 'pinned' | string
  paginate_by?: number
  per_page?: number
  placement?: string
  player_deck_layout?: boolean // Show product card bellow the thumbnail. Defaults to false.
  product_tag_placement?: ProductTagPlacement // [CS-5959] allow placing shopping bag to the bottom
  responsive_thumbnails?: boolean // [CS-9146] responsive carousel for player deck (support in all carousels coming soon)
  /**
   * @public
   * Approximate duration in ms for one full horizontal pass over the scrollable thumbnail track (smooth continuous scroll; `mode=row` only).
   * Larger values scroll slower. `0` or unset disables (default). Ignored for `pinned`, `grid`, etc.
   * Requires Web Animations API (`element.animate`); targets evergreen browsers only.
   */
  horizontal_auto_scroll_interval_ms?: number
  /**
   * @public
   * Carousel thumbnail can alternate between video and card style if thumbnail_style is set to dynamic.
   * There is no way to set card explicitly. This dynamic configuration is suitable for carousels with very few videos.
   * @default "video"
   */
  thumbnail_style?: 'video' | 'dynamic'
  thumbnail_cta?: boolean // Show CTA on top of the thumbnail. Defaults to false.
  thumbnail_orientation?: 'horizontal' | 'vertical' // [CS-8437] allow setting thumbnail orientation
  /**
   * @public
   * Whether to show a product card carousel on hover / focus of the thumbnail.
   * @default false
   */
  thumbnail_product_card?: boolean // Show product card on top of the thumbnail. Defaults to false.
  size?: WidgetSizeEnum // any other string => 'medium', defaults to large
  /**
   * @public
   * When true, hides the horizontal scroll arrow buttons on Carousel / Player Deck
   * so users scroll the thumbnail track by drag/swipe instead. [CS-12234]
   * @default true
   */
  nav_arrows?: boolean
}

export interface IPlayerParams extends IParams {
  cover?: boolean // [CS-3938] cover to render storyblock's main video with 100% width and height
  /**
   * @public
   * When true, completely suppresses storyblocks/players configured with `mode=pinned`
   * (the initial floating minimized layout) to avoid conflict with native SDK player.
   * Intended to be toggled per page via `?fwparam_disable_mode_pinned=true`.
   * @default false
   */
  disable_mode_pinned?: boolean
  dock?: boolean
  height?: string
  mode?: 'pinned' | 'fullscreen' | string
  per_page?: number
  product_card?: boolean // [CS-4595] Whether to show/hide product card on top of the video in the player. Defaults to true.
  /**
   * Livestream only: when to show the on-video product UI (carousel, highlight, pinned-products modal).
   * @default ProductCardLsEnum.ALWAYS (`always`)
   */
  livestream_product_card?: ProductCardLsEnum
  /**
   * Livestream only: controls the size variant of the RemindMe countdown widget.
   * @default 'default'
   */
  remind_me_size?: 'default' | 'small'
  /**
   * Livestream only: when true, hides the visible text label on interaction pills (icon remains).
   * @default false
   */
  hide_interaction_pill_label?: boolean
  width?: string
}

export interface IStoryblockParams extends IPlayerParams {
  /**
   * @public
   * Whether to clear any stored player state from a previous Soft PIP player.
   * @default false
   */
  clear_player_state?: boolean
}

export interface IHerounitParams extends IPlayerParams {
  hero_event_date_max_age_days?: number // Default value is 180 days. 0 hides for any past event, negative never hides
  hero_flip_order?: boolean // Default value is false until explicitly set to true
  hero_hide_countdown?: boolean // Default value is false until explicitly set to true
  hero_previous_event_promotion?: boolean // Default value is true
  hero_text_mode?: 'even' | 'stretched' // Default value is even until explicitly set to stretched
}

export interface AddToCalendarParams extends IParams {
  popup_position?: string
  upcoming_label?: string
  live_label?: string
}

export interface ShareParams extends IParams {
  popup_position?: string
  share_label?: string
}

export interface IStorylinkParams extends IParams {
  per_page?: number
}

export interface IProgrammaticPlayerParams extends IParams {
  appContextType?: AppContextType
  minimized?: boolean
}

export interface ButtonWrapperParams extends IParams {
  button_id?: string
}

export type IAnyWidgetParams =
  | EmbedFeedParams
  | IStoryblockParams
  | IHerounitParams
  | IStorylinkParams
  | IPlayerParams
  | IProgrammaticPlayerParams
  | ShareParams
  | ButtonWrapperParams

export type IAllWidgetParams = EmbedFeedParams &
  IStoryblockParams &
  IHerounitParams &
  IStorylinkParams &
  IPlayerParams &
  IProgrammaticPlayerParams &
  AddToCalendarParams &
  ShareParams &
  ButtonWrapperParams

// @TODO move interface to Widget component
export type IWidgetInitialParams = Partial<IAllWidgetParams>

/**
 *
 */
export const areStoryblockParams = (
  params: IAnyWidgetParams | undefined,
): params is IStoryblockParams => params?.type === WidgetTypesEnum.STORYBLOCK

/**
 *
 */
export const areHeroUnitParams = (
  params: IAnyWidgetParams | undefined,
): params is IHerounitParams => params?.type === WidgetTypesEnum.HEROUNIT

/**
 *
 */
export const arePlayerParams = (
  params: IAnyWidgetParams | undefined,
): params is IPlayerParams => params?.type === WidgetTypesEnum.PLAYER
